import React from "react";
import { ChevronDown } from "lucide-react";
import {
    ThinButton,
    ThinField,
    ThinInput,
    ThinSelect,
    ThinTextarea,
} from "./CompactControls";

export default function SingleItemForm({
    formData,
    setFormData,
    handleChange,
    handleImageChange,
    handleUomChange,
    handleSingleSave,

    showCategoryDropdown,
    setShowCategoryDropdown,
    setCategorySearch,
    filteredCategories,

    showSubcategoryDropdown,
    setShowSubcategoryDropdown,
    setSubcategorySearch,
    filteredSubcategories,

    uomOptions,
    uomLoading,
    sizeOptions,
    sizeLoading,
    getOptionValue,
    getOptionLabel,
    racks,
}) {
    return (
        <div className="rounded-lg border bg-white p-3">
            <div className="mb-3 flex items-center justify-between border-b pb-2">
                <div>
                    <p className="text-sm font-bold text-gray-800">
                        Single Item Details
                    </p>
                    <p className="text-[11px] text-gray-400">
                        Compact form for adding one catalogue item.
                    </p>
                </div>

                <ThinButton variant="primary" onClick={handleSingleSave}>
                    Save Item
                </ThinButton>
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                <ThinField label="Item Name" className="md:col-span-2">
                    <ThinInput
                        name="itemName"
                        value={formData.itemName}
                        onChange={handleChange}
                        placeholder="Item name"
                    />
                </ThinField>

                <ThinField label="Status">
                    <ThinSelect
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                    >
                        <option value="Y">Active</option>
                        <option value="N">Inactive</option>
                    </ThinSelect>
                </ThinField>

                <ThinField label="Ratings">
                    <ThinInput
                        name="rating"
                        value={formData.rating}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>

                <ThinField label="Description" className="md:col-span-4">
                    <ThinTextarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Short item description"
                    />
                </ThinField>

                <ThinField label="Dish / Tags">
                    <ThinInput
                        name="tags"
                        value={formData.tags}
                        onChange={handleChange}
                        placeholder="Tags"
                    />
                </ThinField>

                <ThinField label="Veg / Non-Veg">
                    <ThinSelect
                        name="vegIcon"
                        value={formData.vegIcon}
                        onChange={handleChange}
                    >
                        <option value="">None</option>
                        <option value="Y">Veg</option>
                        <option value="N">Non-Veg</option>
                    </ThinSelect>
                </ThinField>

                <ThinField label="Photo" className="md:col-span-2">
                    <div className="flex items-center gap-2">
                        <input
                            type="file"
                            accept="image/*"
                            className="h-8 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                            onChange={handleImageChange}
                        />

                        {formData.photo && (
                            <div className="relative h-8 w-8 shrink-0">
                                <img
                                    src={formData.photo}
                                    alt="preview"
                                    className="h-8 w-8 rounded-md border object-cover"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            photo: "",
                                        }))
                                    }
                                    className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-red-500 text-[9px] text-white"
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                    </div>
                </ThinField>

                {/* Category */}
                <ThinField label="Category" className="md:col-span-2">
                    <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() =>
                                    !formData.categoryInput &&
                                    setShowCategoryDropdown((prev) => !prev)
                                }
                                className={`flex h-8 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-2 text-left text-xs ${
                                    formData.categoryInput
                                        ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                        : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                <span className="truncate">
                                    {formData.category || "Select Category"}
                                </span>

                                <ChevronDown
                                    size={14}
                                    className={`shrink-0 transition-transform ${
                                        showCategoryDropdown ? "rotate-180" : ""
                                    }`}
                                />
                            </button>

                            {showCategoryDropdown && (
                                <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
                                    <div className="max-h-44 overflow-y-auto">
                                        {filteredCategories.length ? (
                                            filteredCategories.map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            category,
                                                        }));
                                                        setCategorySearch("");
                                                        setShowCategoryDropdown(false);
                                                    }}
                                                    className="w-full px-2 py-1.5 text-left text-xs hover:bg-green-50"
                                                >
                                                    {category}
                                                </button>
                                            ))
                                        ) : (
                                            <div className="px-2 py-2 text-xs text-gray-400">
                                                No category found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <ThinInput
                            placeholder="Or type category"
                            value={formData.categoryInput || ""}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    categoryInput: e.target.value,
                                    category: "",
                                }))
                            }
                        />
                    </div>
                </ThinField>

                {/* Subcategory */}
                <ThinField label="Subcategory" className="md:col-span-2">
                    <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() =>
                                    !formData.subcategoryInput &&
                                    setShowSubcategoryDropdown((prev) => !prev)
                                }
                                className={`flex h-8 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-2 text-left text-xs ${
                                    formData.subcategoryInput
                                        ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                        : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                <span className="truncate">
                                    {formData.subcategory || "Select Subcategory"}
                                </span>

                                <ChevronDown
                                    size={14}
                                    className={`shrink-0 transition-transform ${
                                        showSubcategoryDropdown ? "rotate-180" : ""
                                    }`}
                                />
                            </button>

                            {showSubcategoryDropdown && (
                                <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
                                    <div className="max-h-44 overflow-y-auto">
                                        {filteredSubcategories.length ? (
                                            filteredSubcategories.map((item) => (
                                                <button
                                                    key={item.subCategory}
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            subcategory: item.subCategory,
                                                        }));
                                                        setSubcategorySearch("");
                                                        setShowSubcategoryDropdown(false);
                                                    }}
                                                    className="w-full px-2 py-1.5 text-left text-xs hover:bg-green-50"
                                                >
                                                    {item.subCategory}
                                                </button>
                                            ))
                                        ) : (
                                            <div className="px-2 py-2 text-xs text-gray-400">
                                                No subcategory found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <ThinInput
                            placeholder="Or type subcategory"
                            value={formData.subcategoryInput || ""}
                            disabled={!!formData.subcategory}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    subcategoryInput: e.target.value,
                                    subcategory: "",
                                }))
                            }
                        />
                    </div>
                </ThinField>

                <ThinField label="UOM">
                    <ThinSelect
                        name="uom"
                        value={formData.uom}
                        onChange={handleUomChange}
                        disabled={uomLoading}
                    >
                        <option value="">
                            {uomLoading ? "Loading..." : "Select UOM"}
                        </option>

                        {uomOptions?.map((item, index) => (
                            <option key={index} value={item.uom_code}>
                                {item.uom_name}
                            </option>
                        ))}
                    </ThinSelect>
                </ThinField>

                <ThinField label="Size">
                    <ThinSelect
                        name="size"
                        value={formData.size}
                        onChange={handleChange}
                        disabled={!formData.uom || sizeLoading}
                    >
                        <option value="">
                            {sizeLoading ? "Loading..." : "Select Size"}
                        </option>

                        {sizeOptions?.map((item, index) => (
                            <option key={index} value={getOptionValue(item)}>
                                {getOptionLabel(item)}
                            </option>
                        ))}
                    </ThinSelect>
                </ThinField>

                <ThinField label="Rack">
                    <ThinSelect
                        name="rack"
                        value={formData.rack}
                        onChange={handleChange}
                    >
                        <option value="">Select Rack</option>
                        {racks?.map((r, i) => (
                            <option key={i} value={r.rackNumber}>
                                {r.rackNumber}
                            </option>
                        ))}
                    </ThinSelect>
                </ThinField>

                <ThinField label="Barcode">
                    <ThinInput
                        name="barcode"
                        value={formData.barcode}
                        onChange={handleChange}
                        placeholder="Barcode"
                    />
                </ThinField>

                <ThinField label="Price">
                    <ThinInput
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>

                <ThinField label="Discount">
                    <ThinInput
                        name="discount"
                        value={formData.discount}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>

                <ThinField label="Discounted Price">
                    <ThinInput
                        name="discountedPrice"
                        value={formData.discountedPrice}
                        readOnly
                    />
                </ThinField>

                <ThinField label="Expiry Date">
                    <ThinInput
                        type="date"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleChange}
                    />
                </ThinField>

                <ThinField label="Home Delivery Price">
                    <ThinInput
                        name="homeDeliveryPrice"
                        value={formData.homeDeliveryPrice}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>

                <ThinField label="Takeaway Price">
                    <ThinInput
                        name="takeawayPrice"
                        value={formData.takeawayPrice}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>

                <ThinField label="Dining Price">
                    <ThinInput
                        name="diningPrice"
                        value={formData.diningPrice}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>

                <ThinField label="Room Price">
                    <ThinInput
                        name="roomOrderPrice"
                        value={formData.roomOrderPrice}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>

                <ThinField label="Car Price">
                    <ThinInput
                        name="carOrderPrice"
                        value={formData.carOrderPrice}
                        onChange={handleChange}
                        placeholder="0"
                    />
                </ThinField>
            </div>

            <div className="mt-3 flex justify-end border-t pt-2">
                <ThinButton variant="primary" onClick={handleSingleSave}>
                    Save Item
                </ThinButton>
            </div>
        </div>
    );
}